import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Expand, Trash2, ImagePlus, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Gallery({ photos, isOwner, onSelect, onDelete }) {
  const [armed, setArmed] = useState(null);
  const [chosen, setChosen] = useState(() => new Set());
  const [barArmed, setBarArmed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const toggleChoose = (id) =>
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const confirmDelete = (p) => {
    if (armed === p.id) {
      setArmed(null);
      onDelete(p);
    } else {
      setArmed(p.id);
      setTimeout(() => setArmed((a) => (a === p.id ? null : a)), 3000);
    }
  };

  const removeChosen = async () => {
    if (removing) return;
    if (!barArmed) {
      setBarArmed(true);
      setTimeout(() => setBarArmed(false), 3500);
      return;
    }
    setBarArmed(false);
    setRemoving(true);
    for (const id of Array.from(chosen)) {
      const p = photos.find((x) => x.id === id);
      if (p) await onDelete(p);
    }
    setChosen(new Set());
    setRemoving(false);
  };

  if (!photos.length) {
    return (
      <section id="gallery" data-testid="gallery-section" className="px-6 sm:px-10 lg:px-16 py-24">
        <div className="border border-dashed border-[#3C3527]/25 p-10 sm:p-16 text-center max-w-2xl mx-auto">
          <p className="text-[11px] tracking-[0.35em] uppercase text-[#75332D] mb-4">The Archive</p>
          <h2 className="font-serif font-normal text-3xl sm:text-4xl text-[#3C3527]">
            The gallery is waiting for its first photos.
          </h2>
          <p className="mt-4 text-[#6E6553] font-light max-w-md mx-auto">
            Add the weekend's photos below — they'll be ready to download and print in moments.
          </p>
          <button
            onClick={() => document.getElementById("upload")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="empty-add-photos-btn"
            className="mt-8 inline-flex items-center gap-2 border border-[#3C3527] bg-[#3C3527] text-[#F5F1E6] px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#75332D] hover:border-[#75332D] transition-colors duration-300"
          >
            <ImagePlus size={15} /> Add the first photos
          </button>
        </div>
      </section>
    );
  }

  const ordered = [...photos].sort((a, b) => (a.type === "video" ? 1 : 0) - (b.type === "video" ? 1 : 0));

  return (
    <section id="gallery" data-testid="gallery-section" className="px-6 sm:px-10 lg:px-16 py-24">
      <div className="text-center mb-14">
        <p className="text-[11px] tracking-[0.35em] uppercase text-[#75332D] mb-4">
          {photos.length} frames · newest first
          {isOwner && " · tap the circles to choose photos"}
        </p>
        <h2 className="font-serif font-normal text-4xl sm:text-5xl text-[#3C3527]">The Archive</h2>
      </div>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-8">
        {ordered.map((p, i) => (
          <motion.figure
            key={p.id}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.07 }}
            className={`break-inside-avoid mb-8 group relative ${
              chosen.has(p.id) ? "ring-2 ring-[#75332D] ring-offset-4 ring-offset-[#F5F1E6]" : ""
            }`}
            data-testid={`photo-card-${p.id}`}
          >
            {p.type === "video" ? (
              <div className="relative cursor-pointer" onClick={() => onSelect(p)} data-testid={`video-card-${p.id}`}>
                <video
                  src={`${BACKEND_URL}${p.video_url}#t=0.1`}
                  poster={`${BACKEND_URL}${p.thumb_url}`}
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-auto transition-[filter] duration-500 group-hover:brightness-[.85]"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="bg-[#F5F1E6]/90 backdrop-blur-md p-4 text-[#3C3527]">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5.5v13l11-6.5z" />
                    </svg>
                  </span>
                </div>
              </div>
            ) : (
              <img
                src={`${BACKEND_URL}${p.thumb_url}`}
                alt={p.name}
                loading="lazy"
                onClick={() => onSelect(p)}
                data-testid={`photo-thumb-${p.id}`}
                className="w-full h-auto cursor-zoom-in transition-[filter] duration-500 group-hover:brightness-[.85]"
              />
            )}
            <div
              className={`absolute inset-x-0 top-0 flex items-start justify-between p-3 transition-opacity duration-300 ${
                isOwner ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <div className="flex gap-2">
                {isOwner && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleChoose(p.id);
                      }}
                      data-testid={`select-photo-${p.id}`}
                      title={chosen.has(p.id) ? "Chosen — tap to unchoose" : "Choose this photo"}
                      className={`p-2.5 backdrop-blur-md transition-colors duration-300 ${
                        chosen.has(p.id)
                          ? "bg-[#75332D] text-[#F5F1E6]"
                          : "bg-[#F5F1E6]/85 text-[#3C3527] hover:bg-[#75332D] hover:text-[#F5F1E6]"
                      }`}
                    >
                      <Check size={15} strokeWidth={chosen.has(p.id) ? 3 : 1.5} />
                    </button>
                    <button
                      onClick={() => confirmDelete(p)}
                      data-testid={`delete-photo-${p.id}`}
                      title={armed === p.id ? "Click again to confirm" : "Remove (owner only)"}
                      className={`p-2.5 backdrop-blur-md transition-colors duration-300 ${
                        armed === p.id
                          ? "bg-[#75332D] text-[#F5F1E6]"
                          : "bg-[#F5F1E6]/85 text-[#3C3527] hover:bg-[#75332D] hover:text-[#F5F1E6]"
                      }`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <a
                  href={`${BACKEND_URL}${p.download_url}`}
                  download
                  data-testid={`download-photo-${p.id}`}
                  title="Download this photo"
                  className="bg-[#F5F1E6]/85 backdrop-blur-md p-2.5 text-[#3C3527] hover:bg-[#75332D] hover:text-[#F5F1E6] transition-colors duration-300"
                >
                  <Download size={15} />
                </a>
                <button
                  onClick={() => onSelect(p)}
                  data-testid={`expand-photo-${p.id}`}
                  title="View full screen"
                  className="bg-[#F5F1E6]/85 backdrop-blur-md p-2.5 text-[#3C3527] hover:bg-[#75332D] hover:text-[#F5F1E6] transition-colors duration-300"
                >
                  <Expand size={15} />
                </button>
              </div>
            </div>
            <figcaption className="mt-3 flex items-center justify-between gap-4">
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6E6553] truncate">
                {p.name}
                {isOwner && p.similar_to && <span className="text-[#C4562F]"> · similar to {p.similar_to}</span>}
                {isOwner && p.soft_focus && <span className="text-[#C4562F]"> · soft focus</span>}
              </span>
              <span className="text-[10px] text-[#6E6553] shrink-0">
                {p.width}×{p.height}
              </span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
      {isOwner && chosen.size > 0 && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          data-testid="selection-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-[#3C3527] text-[#F5F1E6] px-6 py-3.5 flex items-center gap-6 shadow-2xl"
        >
          <span className="text-[11px] tracking-[0.2em] uppercase" data-testid="selection-count">
            {chosen.size} chosen
          </span>
          <button
            onClick={removeChosen}
            data-testid="remove-selected-btn"
            className={`text-[11px] tracking-[0.2em] uppercase transition-colors duration-300 ${
              barArmed ? "text-[#C4562F]" : "hover:text-[#C4562F]"
            }`}
          >
            {removing ? "Removing…" : barArmed ? "Tap again to confirm" : "Remove them"}
          </button>
          <button
            onClick={() => setChosen(new Set())}
            data-testid="clear-selection-btn"
            className="text-[11px] tracking-[0.2em] uppercase opacity-60 hover:opacity-100 transition-opacity duration-300"
          >
            Clear
          </button>
        </motion.div>
      )}
    </section>
  );
}
